/** PC 端布局尺寸（三栏 SPA） */
export const PC_SIDEBAR_WIDTH = 76
export const PC_LIST_PANEL_WIDTH = 310
export const PC_LIST_PANEL_WIDTH_MIN = 300
export const PC_LIST_PANEL_WIDTH_MAX = 380
export const PC_LIST_PANEL_WIDTH_KEY = "pc_list_panel_width"

/** 「我的」Tab 第二栏固定宽度 */
export const PC_ME_PANEL_WIDTH = 380

export function getStoredListPanelWidth(): number {
  if (typeof localStorage === "undefined") return PC_LIST_PANEL_WIDTH
  const stored = localStorage.getItem(PC_LIST_PANEL_WIDTH_KEY)
  if (!stored) return PC_LIST_PANEL_WIDTH
  const n = Number(stored)
  if (!Number.isFinite(n)) return PC_LIST_PANEL_WIDTH
  return Math.min(
    PC_LIST_PANEL_WIDTH_MAX,
    Math.max(PC_LIST_PANEL_WIDTH_MIN, Math.round(n)),
  )
}

export function storeListPanelWidth(width: number) {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(PC_LIST_PANEL_WIDTH_KEY, String(width))
  }
}

/** 弹窗、浮层等内容区最大宽度（PC 居中弹窗） */
export const MODAL_MAX_WIDTH = 480

/** @deprecated 保留兼容；PC 版主界面为全宽，不再使用手机壳宽度 */
export const APP_MAX_WIDTH = MODAL_MAX_WIDTH

/** 全屏 fixed 浮层：PC 铺满视口 */
export const appFixedViewportStyle = {
  position: "fixed" as const,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  width: "100%",
  maxWidth: "none" as const,
}

/** PC 钱包 iframe：保留左侧导航栏，内容区紧贴侧栏右缘 */
export const pcWebviewLayerStyle = {
  position: "fixed" as const,
  top: 0,
  bottom: 0,
  left: "var(--pc-sidebar-width)",
  right: 0,
  width: "calc(100% - var(--pc-sidebar-width))",
  maxWidth: "none" as const,
}

/** PC 钱包 iframe：仅第三栏（侧栏 + 列表面板之后） */
export const pcDetailWebviewLayerStyle = {
  position: "fixed" as const,
  top: 0,
  bottom: 0,
  left: "calc(var(--pc-sidebar-width) + var(--pc-list-panel-width))",
  right: 0,
  width: "calc(100% - var(--pc-sidebar-width) - var(--pc-list-panel-width))",
  maxWidth: "none" as const,
}

/** 列表侧栏当前 Tab 的 session 存储键 */
export const PC_ACTIVE_TAB_KEY = "pc_active_tab"

/** 进入通知中心前的返回路径 */
export const PC_NOTICE_RETURN_KEY = "pc_notice_return_path"

export const PC_TAB_PATHS = ["/chats", "/contacts", "/groups", "/me"] as const
export type PcTabPath = (typeof PC_TAB_PATHS)[number]

/** 「我的」Tab 下在第三栏展示的子路由 */
export const ME_SUB_PATHS = new Set([
  "/settings",
  "/profile",
  "/my-account",
  "/privacy",
  "/security-google",
  "/group-assistant",
  "/not-disturb",
  "/notification-bell",
  "/language",
  "/fontsize",
  "/login-device",
  "/clear-cache",
  "/online-time",
  "/self-destruct",
  "/add-method",
  "/black-list",
  "/update-email",
  "/update-gender",
  "/avatar",
])

export function isPcTabPath(path: string): path is PcTabPath {
  return (PC_TAB_PATHS as readonly string[]).includes(path)
}

export function getActivePcTab(pathname: string): PcTabPath {
  return isPcTabPath(pathname) ? pathname : getStoredPcTab()
}

export function getStoredPcTab(): PcTabPath {
  if (typeof sessionStorage === "undefined") return "/chats"
  const stored = sessionStorage.getItem(PC_ACTIVE_TAB_KEY)
  return isPcTabPath(stored ?? "") ? stored : "/chats"
}

export function storePcTab(path: PcTabPath) {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(PC_ACTIVE_TAB_KEY, path)
  }
}

/** 记录进入通知中心前的列表页（聊天/好友/群聊 Tab） */
export function storeNoticeReturnPath(pathname: string) {
  if (typeof sessionStorage === "undefined") return
  const path = isPcTabPath(pathname) ? pathname : getStoredPcTab()
  sessionStorage.setItem(PC_NOTICE_RETURN_KEY, path)
}

/** 离开通知中心时应回到的列表 Tab，默认聊天列表 */
export function getNoticeReturnPath(): PcTabPath {
  if (typeof sessionStorage === "undefined") return "/chats"
  const stored = sessionStorage.getItem(PC_NOTICE_RETURN_KEY)
  if (isPcTabPath(stored ?? "")) return stored as PcTabPath
  return getStoredPcTab()
}

/** 是否处于「我的」双栏布局（第二栏 Me 页，第三栏子页/水印） */
export function isMeTabContext(pathname: string) {
  if (pathname === "/me") return true
  if (!ME_SUB_PATHS.has(pathname)) return false
  return getStoredPcTab() === "/me"
}

/** 通知中心：列表在第二栏，详情在第三栏 */
export function isNoticeCenterRoute(pathname: string) {
  return pathname === "/notices"
}

/** 子路由在详情区展示（非 Tab 页） */
export function isDetailRoute(pathname: string) {
  if (pathname === "/" || isPcTabPath(pathname)) return false
  if (isNoticeCenterRoute(pathname)) return false
  if (pathname === "/me") return false
  return true
}
